import type { Asset, Game } from "../data/types";

/**
 * Where a tab's cover art lives — `asset-cover-art` FR-12.
 *
 * Covers are the one file type served **unsigned**. Adam's call, 2026-09-12:
 * nothing about them is sensitive, and the tier matrix already agreed — the
 * `tab:cover` row grants cover art to every tier including 8 Bit, so signing it
 * protected nothing. That is what makes NFR-1 free: a cover's URL is derived
 * from data the client already holds, so a grid of N cards costs no API calls
 * at all, rather than N of them through `getDownloadUrl`.
 *
 * ### Why this returns `null` rather than a best effort
 *
 * Everything downstream treats "no URL" as "draw the sprite", and that is a
 * good state — it is what most tabs look like and always will. A *half-built*
 * URL is not that. `${undefined}/${key}` is a string, so it renders an `<img>`
 * that 404s, and the reader sees a broken-image glyph where the design says
 * there should be a tinted sprite.
 *
 * The base URL is absent in exactly the situations where nobody would think to
 * look: an environment deployed before this shipped, a creator payload that
 * failed to carry it, a test that did not set it. So the guard is not
 * defensive programming against the impossible — it is the ordinary case for
 * every environment until the backend half lands.
 */
export const COVER_SLOT = "cover";

/**
 * One storage key, resolved against the public base.
 *
 * Shared by the tab's cover and the game's, so the two cannot disagree about
 * escaping or about what an absent base means.
 */
export function urlForKey(
  key: string | undefined,
  baseUrl: string | undefined
): string | null {
  if (!key || !baseUrl) return null;
  // A base with or without a trailing slash is the same base. Getting this
  // wrong yields `host//key`, which S3 reads as an empty first path segment and
  // answers with a 404 — a working URL shape that fetches nothing.
  return `${baseUrl.replace(/\/+$/, "")}/${key.split("/").map(encodeURIComponent).join("/")}`;
}

export function coverUrl(
  asset: Pick<Asset, "files">,
  baseUrl: string | undefined
): string | null {
  return urlForKey(asset.files?.[COVER_SLOT]?.key, baseUrl);
}

/**
 * Every image that could stand in for a tab, best first — `game-cover-art`
 * FR-2, FR-3.
 *
 * The tab's own cover, then its game's, and nothing else. The sprite is not in
 * here: it is not a URL, it is what `AssetArt` draws when this list runs out.
 *
 * ### The order is the product
 *
 * **Tab first.** Adam's call, 2026-09-13, asked before building rather than
 * assumed — his first phrasing put the game first, which would have made
 * per-tab art unreachable: a tab whose game had art could never show its own,
 * and the only way to override a default would be to delete it.
 *
 * Reversing these two lines is a one-word change that produces a product which
 * looks like it works and quietly refuses to be overridden. `artCandidates`
 * exists so there is exactly one line to get right, rather than one per screen.
 *
 * ### Why a list rather than a preferred URL and a fallback
 *
 * Because a *failed load* has to advance one step, not skip to the end (FR-4).
 * A tab whose cover 404s should show its game's art. Expressed as "the URL,
 * plus a fallback", the natural implementation catches the error and draws the
 * sprite — which behaves perfectly on every tab whose cover loads, and is wrong
 * only for the broken ones nobody is looking at while testing.
 */
export function artCandidates(
  asset: Pick<Asset, "files">,
  game: Pick<Game, "coverKey"> | undefined,
  baseUrl: string | undefined
): string[] {
  return [coverUrl(asset, baseUrl), urlForKey(game?.coverKey, baseUrl)].filter(
    (url): url is string => url !== null
  );
}
